import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import GroupIcon from '@mui/icons-material/Group';
import { useNavigate } from 'react-router-dom';
import { useDemoRole } from '../../../contexts/DemoRoleContext';
import { canAccessToolPath } from '../utils/accessControl';

/**
 * DemoAccessGate — wraps a routed page and blocks it if the current demo persona
 * isn't permitted to view it (per accessControl.js). Reached only when someone
 * navigates directly to a restricted route; the sidebar already hides the links.
 *
 * Renders a friendly "not available at this access level" notice rather than a
 * hard error, so the demo recovers gracefully. The Administrator persona has
 * full access, so this never blocks the default demo identity.
 */
export default function DemoAccessGate({ path, children }) {
  const { persona, canViewAs } = useDemoRole();
  const navigate = useNavigate();

  if (canAccessToolPath(persona?.id, path)) {
    return children;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 540, textAlign: 'center' }}>
        <LockPersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Not available at this access level
        </Typography>
        {canViewAs ? (
          // Org admin previewing a lens — they can switch back themselves.
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The <strong>{persona?.label || 'current'}</strong> role doesn&rsquo;t have
            access to this view. Use the <GroupIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} />{' '}
            <strong>View as</strong> selector in the top bar to switch to a role with
            the right permissions — or choose <strong>View as yourself</strong> for full access.
          </Typography>
        ) : (
          // A real scoped member — this simply isn't part of their role.
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This view isn&rsquo;t part of the <strong>{persona?.label || 'current'}</strong>{' '}
            role. If you think you need access, ask your organization&rsquo;s administrator.
          </Typography>
        )}
        <Button variant="contained" onClick={() => navigate('/drew')}>
          Back to OE Dashboard
        </Button>
      </Paper>
    </Box>
  );
}
